import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Eye } from "lucide-react";
import { toast } from "sonner";

import BasicTab from "./builder/tabs/BasicTab";
import DimensionsTab from "./builder/tabs/DimensionsTab";
import DemographicsTab from "./builder/tabs/DemographicsTab";
import SectionsTab from "./builder/tabs/SectionsTab";
import QuestionsTab from "./builder/tabs/QuestionsTab";
import ScoringTab from "./builder/tabs/ScoringTab";
import NormsTab from "./builder/tabs/NormsTab";
import FormulasTab from "./builder/tabs/FormulasTab";
import ResultTab from "./builder/tabs/ResultTab";
import VersionsPanel from "./builder/VersionsPanel";
import PublicationReadiness from "./builder/PublicationReadiness";
import ResultPreviewDialog from "./builder/ResultPreviewDialog";
import { issueMessage, persistInstrumentWithPublishGuard } from "./builder/publicationValidation";
import { removeGroupedDimension, renameGroupedDimension } from "./builder/groupedCombination";
import { removeDimensionScoringReferences, setDimensionInterpretationBasis } from "./builder/scoringCriteria";
import {
  addMatrixRowAt,
  addOptionAt,
  appendSection,
  appendQuestion,
  changeQuestionTypeAt,
  duplicateQuestionAt,
  moveSectionAt,
  mergeInstrumentForEditing,
  moveOptionAt,
  removeDimensionFromQuestions,
  removeMatrixRowAt,
  removeOptionAt,
  removeQuestionAt,
  removeSectionAt,
  updateMatrixRowAt,
  updateOptionAt,
  updateQuestionAt,
  updateSectionAt,
} from "./builder/questionBuilder";

const uid = () => crypto.randomUUID();

const emptyInstrument = () => ({
  slug: "", name_id: "", name_en: "",
  short_description_id: "", short_description_en: "",
  description_id: "", description_en: "",
  category_id: null, cover_image: "", cover_image_en: "",
  duration_minutes: 5, target_age_min: 13, target_age_max: 99,
  development_status: "explorasi", status: "draft",
  instructions_id: "", instructions_en: "",
  disclaimer_id: "Alat ini bersifat edukatif untuk eksplorasi diri, bukan diagnosis.",
  disclaimer_en: "This tool is for self-exploration and is not a diagnosis.",
  questions_per_page: 1, randomize_questions: false, allow_download: true,
  dimensions: [], sections: [], questions: [],
  scoring: { method: "continuous_multi", scale_min: 1, scale_max: 5, criteria: [], norms: [], formulas: {}, combined_dimensions: [] },
  chart_config: { type: "bar_horizontal", show_percentage: true, show_raw_score: false, show_top_only: false, top_n: 3, include_in_pdf: true, include_in_social: true },
  share_config: { public_share_enabled: false, social_card_enabled: true, identity_display: "none" },
  pdf_config: { brand_name: "Mindovia", show_respondent_info: false, show_raw_scores: false, show_chart: true, show_interpretation: true, email_required: false },
  ai_config: { enabled: false, prompt_id: "", prompt_en: "" },
  result_template: { show_dimension_scores: true, show_total: false, allow_download: true, show_respondent_info: false, intro_id: "", intro_en: "", footer_id: "", footer_en: "" },
});

export default function InstrumentBuilder() {
  const { id } = useParams();
  const isNew = id === undefined;
  const { lang } = useLang();
  const nav = useNavigate();
  const [inst, setInst] = useState(emptyInstrument());
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState("basic");
  const [saving, setSaving] = useState(false);
  const [checkingReadiness, setCheckingReadiness] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [warningConfirmationFingerprint, setWarningConfirmationFingerprint] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/categories").then(r => setCategories(r.data));
    if (!isNew) {
      api.get(`/admin/instruments/${id}`).then(r => {
        const d = r.data;
        setInst(mergeInstrumentForEditing(emptyInstrument(), d));
      }).catch(() => toast.error("Not found"));
    }
  }, [id, isNew]);

  const instrumentFingerprint = JSON.stringify(inst);
  const readinessStale = !!readiness && readiness.fingerprint !== instrumentFingerprint;
  const confirmingWarnings = warningConfirmationFingerprint === instrumentFingerprint;

  const requestReadiness = async () => {
    const payload = inst;
    const fingerprint = JSON.stringify(payload);
    setCheckingReadiness(true);
    try {
      const { data } = await api.post("/admin/instrument-validation", payload);
      setReadiness({ result: data, fingerprint });
      return data;
    } finally {
      setCheckingReadiness(false);
    }
  };

  const checkReadiness = async () => {
    try {
      await requestReadiness();
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail) || error.message);
    }
  };

  const openIssue = (issue) => {
    if (issue?.tab) setTab(issue.tab);
  };

  const save = async (publish = null) => {
    setSaving(true);
    if (publish) setCheckingReadiness(true);
    try {
      const outcome = await persistInstrumentWithPublishGuard({
        client: api,
        instrument: inst,
        publish: !!publish,
        allowWarnings: !!publish && confirmingWarnings,
        isNew,
        instrumentId: id,
      });
      if (outcome.validation) setReadiness({ result: outcome.validation, fingerprint: JSON.stringify(inst) });
      if (outcome.kind === "warnings") {
        setWarningConfirmationFingerprint(JSON.stringify(inst));
        toast.warning(lang === "id" ? "Tinjau peringatan, lalu pilih Publikasikan tetap." : "Review the warnings, then choose Publish anyway.");
        return;
      }
      if (outcome.kind === "blocked") {
        setWarningConfirmationFingerprint(null);
        const firstError = outcome.validation?.errors?.[0];
        if (firstError?.tab) setTab(firstError.tab);
        toast.error(firstError ? issueMessage(firstError, lang) : (lang === "id" ? "Instrumen belum siap dipublikasikan." : "Instrument is not ready to publish."));
        return;
      }
      setWarningConfirmationFingerprint(null);
      toast.success(lang === "id" ? "Tersimpan" : "Saved", {
        action: { label: lang === "id" ? "Preview hasil" : "Preview result", onClick: () => setPreviewOpen(true) },
      });
      if (isNew) nav(`/admin/instruments/${outcome.response.data.id}/edit`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
    finally {
      setSaving(false);
      if (publish) setCheckingReadiness(false);
    }
  };

  const set = (k, v) => setInst((p) => ({ ...p, [k]: v }));

  // Dimensions
  const addDimension = () => set("dimensions", [...inst.dimensions, { id: uid(), code: "", name_id: "", name_en: "", description_id: "", description_en: "" }]);
  const updDim = (i, patch) => {
    const arr = [...inst.dimensions];
    const previous = arr[i];
    arr[i] = { ...arr[i], ...patch };
    setInst((current) => {
      let resolver = current.result_key_resolver;
      if (patch.code !== undefined && previous.code !== patch.code && resolver?.mode === "grouped_combination") {
        resolver = renameGroupedDimension(resolver, previous.id, previous.code, patch.code);
      }
      return { ...current, dimensions: arr, result_key_resolver: resolver };
    });
  };
  const rmDim = (i) => {
    const removed = inst.dimensions[i];
    setInst((current) => ({
      ...current,
      dimensions: current.dimensions.filter((_, index) => index !== i),
      questions: removeDimensionFromQuestions(current.questions, removed.id),
      scoring: removeDimensionScoringReferences(current.scoring, removed.id),
      result_key_resolver: current.result_key_resolver?.mode === "grouped_combination"
        ? removeGroupedDimension(current.result_key_resolver, removed.id)
        : current.result_key_resolver,
    }));
  };

  // Sections
  const addSection = () => setInst((current) => ({ ...current, sections: appendSection(current.sections) }));
  const updSection = (i, patch) => setInst((current) => ({ ...current, sections: updateSectionAt(current.sections, i, patch) }));
  const moveSection = (i, direction) => setInst((current) => ({ ...current, sections: moveSectionAt(current.sections, i, direction) }));
  const rmSection = (i) => setInst((current) => ({ ...current, ...removeSectionAt(current.sections, current.questions, i) }));

  // Questions
  const addQuestion = (sectionId = null) => setInst((current) => ({
    ...current,
    questions: appendQuestion(current.questions, sectionId),
  }));
  const updQuestion = (i, patch) => set("questions", updateQuestionAt(inst.questions, i, patch));
  const rmQuestion = (i) => set("questions", removeQuestionAt(inst.questions, i));
  const duplicateQuestion = (i) => set("questions", duplicateQuestionAt(inst.questions, i));
  const changeQuestionType = (i, newType) => set("questions", changeQuestionTypeAt(inst.questions, i, newType));
  const addOption = (qi) => set("questions", addOptionAt(inst.questions, qi));
  const updOption = (qi, oi, patch) => set("questions", updateOptionAt(inst.questions, qi, oi, patch));
  const rmOption = (qi, oi) => set("questions", removeOptionAt(inst.questions, qi, oi));
  const moveOption = (qi, oi, direction) => set("questions", moveOptionAt(inst.questions, qi, oi, direction));
  const addMatrixRow = (qi) => set("questions", addMatrixRowAt(inst.questions, qi));
  const updMatrixRow = (qi, ri, patch) => set("questions", updateMatrixRowAt(inst.questions, qi, ri, patch));
  const rmMatrixRow = (qi, ri) => set("questions", removeMatrixRowAt(inst.questions, qi, ri));

  // Scoring criteria
  const criteriaByDim = useMemo(() => {
    const map = new Map();
    (inst.scoring.criteria || []).forEach((c) => map.set(c.dimension_id || "_overall", c));
    return map;
  }, [inst.scoring]);
  const setCriteriaRanges = (dimId, ranges) => {
    const list = [...(inst.scoring.criteria || [])];
    const key = dimId || null;
    const idx = list.findIndex((c) => (c.dimension_id || null) === key);
    if (idx >= 0) list[idx] = { ...list[idx], ranges }; else list.push({ dimension_id: key, interpretation_basis: "raw_score", ranges });
    set("scoring", { ...inst.scoring, criteria: list });
  };
  const setCriteriaBasis = (dimId, basis) => set("scoring", setDimensionInterpretationBasis(inst.scoring, dimId, basis));
  const addRange = (dimId) => { const existing = criteriaByDim.get(dimId || "_overall")?.ranges || []; setCriteriaRanges(dimId, [...existing, { code: "", min: 0, max: 0, label_id: "", label_en: "", interpretation_id: "", interpretation_en: "", recommendation_id: "", recommendation_en: "" }]); };
  const updRange = (dimId, ri, patch) => { const existing = [...(criteriaByDim.get(dimId || "_overall")?.ranges || [])]; existing[ri] = { ...existing[ri], ...patch }; setCriteriaRanges(dimId, existing); };
  const rmRange = (dimId, ri) => { const existing = [...(criteriaByDim.get(dimId || "_overall")?.ranges || [])]; existing.splice(ri, 1); setCriteriaRanges(dimId, existing); };

  // Norms
  const normByDim = useMemo(() => { const m = new Map(); (inst.scoring.norms || []).forEach(n => m.set(n.dimension_id, n)); return m; }, [inst.scoring]);
  const setNorm = (dimId, patch) => {
    const list = [...(inst.scoring.norms || [])];
    const idx = list.findIndex(n => n.dimension_id === dimId);
    if (idx >= 0) list[idx] = { ...list[idx], ...patch };
    else list.push({ dimension_id: dimId, mean: 0, sd: 1, ...patch });
    set("scoring", { ...inst.scoring, norms: list });
  };

  // Formulas
  // Norms and formulas are rarely used; their tabs only show for tests that already have them.
  const hasNorms = (inst.scoring.norms || []).length > 0;
  const hasFormulas = Object.keys(inst.scoring.formulas || {}).length > 0;
  const setFormula = (target, expr) => set("scoring", { ...inst.scoring, formulas: { ...(inst.scoring.formulas || {}), [target]: expr } });
  const rmFormula = (target) => { const f = { ...(inst.scoring.formulas || {}) }; delete f[target]; set("scoring", { ...inst.scoring, formulas: f }); };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <button onClick={() => nav("/admin/instruments")} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mb-2" data-testid="builder-back-btn"><ArrowLeft className="w-3 h-3" /> {lang === "id" ? "Kembali" : "Back"}</button>
          <h1 className="font-display text-3xl tracking-tight">{isNew ? (lang === "id" ? "Instrumen Baru" : "New Instrument") : (lang === "id" ? "Edit Instrumen" : "Edit Instrument")}</h1>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
            <Badge variant="outline">{inst.status}</Badge>
            <span>{inst.questions.length} {lang === "id" ? "pertanyaan" : "questions"}</span>
            <span>·</span>
            <span>{inst.dimensions.length} {lang === "id" ? "dimensi" : "dimensions"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="builder-preview-result" variant="ghost" onClick={() => setPreviewOpen(true)} disabled={saving}>
            <Eye className="w-4 h-4 mr-2" /> {lang === "id" ? "Preview Hasil" : "Preview Result"}
          </Button>
          <Button data-testid="builder-save-draft" variant="outline" onClick={() => save(false)} disabled={saving}>{lang === "id" ? "Simpan Draft" : "Save Draft"}</Button>
          <Button data-testid="builder-publish" onClick={() => save(true)} disabled={saving || checkingReadiness} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
            {confirmingWarnings
              ? (lang === "id" ? "Publikasikan tetap" : "Publish anyway")
              : (lang === "id" ? "Simpan & Publikasikan" : "Save & Publish")}
          </Button>
        </div>
      </div>

      <PublicationReadiness
        result={readiness?.result || null}
        stale={readinessStale}
        checking={checkingReadiness}
        onCheck={checkReadiness}
        onIssue={openIssue}
        lang={lang}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="basic" data-testid="tab-basic">{lang === "id" ? "Dasar" : "Basic"}</TabsTrigger>
          <TabsTrigger value="dimensions" data-testid="tab-dimensions">{lang === "id" ? "Dimensi" : "Dimensions"}</TabsTrigger>
          <TabsTrigger value="demographics" data-testid="tab-demographics">{lang === "id" ? "Demografi" : "Demographics"}</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">{lang === "id" ? "Bagian" : "Sections"}</TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions">{lang === "id" ? "Pertanyaan" : "Questions"}</TabsTrigger>
          <TabsTrigger value="scoring" data-testid="tab-scoring">Scoring</TabsTrigger>
          {hasNorms && <TabsTrigger value="norms" data-testid="tab-norms">Norms</TabsTrigger>}
          {hasFormulas && <TabsTrigger value="formulas" data-testid="tab-formulas">Formulas</TabsTrigger>}
          <TabsTrigger value="result" data-testid="tab-result">{lang === "id" ? "Hasil" : "Result"}</TabsTrigger>
          {!isNew && <TabsTrigger value="versions" data-testid="tab-versions">Versions</TabsTrigger>}
        </TabsList>

        <TabsContent value="basic">
          <BasicTab inst={inst} set={set} categories={categories} lang={lang} />
        </TabsContent>
        <TabsContent value="dimensions">
          <DimensionsTab inst={inst} addDimension={addDimension} updDim={updDim} rmDim={rmDim} lang={lang} />
        </TabsContent>
        <TabsContent value="demographics">
          <DemographicsTab inst={inst} set={set} lang={lang} />
        </TabsContent>
        <TabsContent value="sections">
          <SectionsTab inst={inst} addSection={addSection} updSection={updSection} moveSection={moveSection} rmSection={rmSection} lang={lang} />
        </TabsContent>
        <TabsContent value="questions">
          <QuestionsTab
            inst={inst}
            set={set}
            addSection={addSection}
            addQuestion={addQuestion}
            updQuestion={updQuestion}
            rmQuestion={rmQuestion}
            duplicateQuestion={duplicateQuestion}
            changeQuestionType={changeQuestionType}
            addOption={addOption}
            updOption={updOption}
            rmOption={rmOption}
            moveOption={moveOption}
            addMatrixRow={addMatrixRow}
            updMatrixRow={updMatrixRow}
            rmMatrixRow={rmMatrixRow}
            lang={lang}
          />
        </TabsContent>
        <TabsContent value="scoring">
          <ScoringTab inst={inst} set={set} criteriaByDim={criteriaByDim} setCriteriaBasis={setCriteriaBasis} addRange={addRange} updRange={updRange} rmRange={rmRange} lang={lang} />
        </TabsContent>
        <TabsContent value="norms">
          <NormsTab inst={inst} normByDim={normByDim} setNorm={setNorm} />
        </TabsContent>
        <TabsContent value="formulas">
          <FormulasTab inst={inst} setFormula={setFormula} rmFormula={rmFormula} />
        </TabsContent>
        <TabsContent value="result">
          <ResultTab inst={inst} set={set} lang={lang} instrumentId={id} />
        </TabsContent>
        {!isNew && <TabsContent value="versions"><VersionsPanel instrumentId={id} /></TabsContent>}
      </Tabs>

      <ResultPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} instrument={inst} lang={lang} />
    </AdminLayout>
  );
}
